import type { AdChatConfig, SaveAdChatParams } from '@module/apis/client-side/chat/type';
import dayjs, { type Dayjs } from 'dayjs';

interface BuildSuspiciousAdChatLogParams {
  now: Dayjs;
  message: string;
  replacedChat: string;
  isAdChat: boolean;
  adConfig: AdChatConfig | undefined;
  user: { purchaseUser?: 'Y' | 'N' } | undefined;
  fan: { isRoomMaster: boolean; isManager: boolean; level: number };
  bjIdx: number;
  myIdx: number | undefined;
  mediaCode: string;
  roomEnterTime: string;
  lastKeyPressed: string;
  typingStart: string;
}

/**
 * 광고성 의심 유저의 채팅을 서버 저장용 파라미터(SaveAdChatParams)로 변환한다.
 * 저장 대상이 아니면 null을 반환한다 — 호출처는 null이면 저장하지 않는다.
 *
 * 순수 함수 — 현재 시각(now)을 주입받아 시간 계산을 결정적으로 만든다.
 * 동작은 useChatComposer의 기존 광고성 채팅 저장 로직과 동일하다
 * (now를 포맷한 뒤 재파싱하여 비교하는 기존 동작 포함).
 */
const buildSuspiciousAdChatLog = ({
  now,
  message,
  replacedChat,
  isAdChat,
  adConfig,
  user,
  fan,
  bjIdx,
  myIdx,
  mediaCode,
  roomEnterTime,
  lastKeyPressed,
  typingStart,
}: BuildSuspiciousAdChatLogParams): SaveAdChatParams | null => {
  // 광고성 의심 유저 판정: 비결제 + 방장/매니저 아님 + 본인 방 아님 + 저장 설정 ON + 팬 레벨 0
  const isSuspiciousUser =
    user?.purchaseUser === 'N' &&
    !fan.isRoomMaster &&
    !fan.isManager &&
    bjIdx !== myIdx &&
    adConfig?.saveAdChat &&
    fan.level === 0;

  if (!isSuspiciousUser || !adConfig) return null;

  const currentDayjsFull = now.format('YYYY-MM-DD HH:mm:ss');
  const currentDayjsMs = now.format('YYYY-MM-DD HH:mm:ss.SSS');
  const timeToFirstChat = dayjs(currentDayjsFull).diff(roomEnterTime, 'second');
  const messageLength = message.length;

  // 입장 직후(chatFilterTime 이내) + 일정 길이(chatFilterText 이상)일 때만 저장 대상
  if (timeToFirstChat >= adConfig.chatFilterTime || messageLength < adConfig.chatFilterText) {
    return null;
  }

  const typingPerSec = (messageLength * 1000) / dayjs(currentDayjsMs).diff(typingStart, 'millisecond');

  return {
    replacedChat,
    roomUserIdx: bjIdx,
    mediaCode,
    roomEnterTime,
    chatTime: currentDayjsFull,
    chatFilterTime: adConfig.chatFilterTime,
    chatFilterText: adConfig.chatFilterText,
    keyControl: lastKeyPressed,
    typingPerSec,
    adChat: isAdChat ? 'Y' : 'N',
    chat: message,
  };
};

export default buildSuspiciousAdChatLog;
